/**
Copyright (C) 2019-2022, Infraclear Inc. All Rights Reserved.
*/

import React, { useState } from "react";
import "./generateBarcode.scss";
import { MessageText } from "../../constants/messageText";
import { Button, TextField, IconButton, CircularProgress } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import instance from "../../utils/axiosInstance";
import { SnackBarMessage } from "../snackBar/snackBar";
import {
  associateGoogleTOTP,
} from "../../constants/api-urlConstants";
import { SetBarcode } from "../setBarcode/setBarcode";
import CloseIcon from "@mui/icons-material/Close";
import { ErrorCodes } from "../../constants/messageCodes";
import { encryptpassword } from "../../constants/commonConstants";
export const GenerateBarcode: React.FC<any> = ({
  barcodeChangeSuccessfully,
  genCodeClose,
}) => {
  const [msgConfig, setMsgConfig] = useState({
    type: "",
    message: "",
    snackFlag: false,
  });
  const [showPassword, setShowPassword] = useState<any>(false);
  const [barcodeLoading, setBarcodeLoading] = React.useState(false);
  const [showActivate, setShowActivate] = React.useState(false);
  const [qrCode, setQrCode] = useState<any>("");
  const [statusIsLoading, setStatusIsLoading] = React.useState(false);
  const [credentialsObj, setCredentialsObj] = React.useState({});
  const [initial_state, setInitial_state] = useState<any>({
    email: "",
    password: "",
  });

  const generateBarcode = Yup.object().shape({
    email: Yup.string()
      .email(MessageText.VALIDATION_EMAIL_FORMAT)
      .required(MessageText.VALIDATION_EMAIL_REQUIRED),
    password: Yup.string().required(MessageText.VALIDATION_PASS_REQUIRED_LOGIN),
  });

  const onFinish = (credentials: any) => {
    if (credentials) {
      generateMFA(credentials);
    }
  };

  const generateMFA = async (credentials: any) => {
    setStatusIsLoading(true);
    const encrypt_newPassword: any = await encryptpassword(credentials.password);
    const MFApayload = {
      email: credentials.email,
      password: encrypt_newPassword
    };
    setCredentialsObj(credentials);
    const USER_API_BASE_URL = `${associateGoogleTOTP}`;

    instance
      .post(USER_API_BASE_URL, MFApayload)
      .then((response) => {
        setBarcodeLoading(false);
        const res = response.data;
        setStatusIsLoading(false);
        if (res.success) {
          setQrCode(res.data);
          setShowActivate(res.data.validated);
          setBarcodeLoading(true);
        } else if (res.message_code === ErrorCodes.INVALID_USERNAME_PASSWORD) {
          setMsgConfig({
            type: "error",
            message: MessageText.USERNAME_PASS_NO_MATCH,
            snackFlag: true,
          });
        } else if (res.message_code === ErrorCodes.MFA_NOT_SUPPORTED) {
          setMsgConfig({
            type: "error",
            message: MessageText.TRIAL_USER_AUTHENTICATION_CODE_HELP_MESSAGE,
            snackFlag: true,
          });
        } else {
          setMsgConfig({
            type: "error",
            message: MessageText.WENT_WRONG,
            snackFlag: true,
          });
        }
      })
      .catch((error) => {
        setStatusIsLoading(false);
      });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const hideGenBarcode = () => {
    genCodeClose();
  };
  const goToLogin = (value: any = false) => {
    const obj = {
      showMsg: value,
      hideChangepass: false,
    };
    barcodeChangeSuccessfully(obj);
  };

  return (
    <div className="generateBarcodeWrapper">
      {!barcodeLoading && (
        <div className="generateBarcodeWrapperHead">
          <div className="headerWrapper">
            <span className="changePasswordtitle">
              {MessageText.RESET_MFA}
            </span>
            <IconButton
              aria-label="close"
              className="closePosition"
              onClick={hideGenBarcode}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>
          <Formik
            enableReinitialize={true}
            initialValues={initial_state}
            validationSchema={generateBarcode}
            onSubmit={onFinish}
          >
            {({
              touched,
              errors,
              values,
              handleBlur,
              handleChange,
              handleSubmit,
            }) => (
              <Form onSubmit={handleSubmit}>
                <Field
                  as={TextField}
                  margin="normal"
                  error={touched.email && Boolean(errors.email)}
                  fullWidth
                  label="Email*"
                  name="email"
                  autoComplete="email"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    autoComplete: "new-email",
                    form: {
                      autoComplete: "off",
                    },
                  }}
                  helperText={<ErrorMessage name="email" />}
                />
                <Field
                  as={TextField}
                  margin="normal"
                  fullWidth
                  name="password"
                  label="Password*"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  autoComplete="off"
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  onChange={handleChange}
                  value={values.password}
                  error={touched.password && Boolean(errors.password)}
                  helperText={<ErrorMessage name="password" />}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton tabIndex={-1}
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <div className="loader">
                  {statusIsLoading && (
                    <div>
                      <CircularProgress size={25} />
                    </div>
                  )}
                  {!statusIsLoading && (
                    <Button
                      type="submit"
                      variant="contained"
                      className="submitBtn"
                      style={{ textTransform: "none" }}
                    >
                      Reset
                    </Button>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}
      {barcodeLoading && (
        <SetBarcode
          qrCodeData={qrCode}
          credentialsObjData={credentialsObj}
          showActivateForm={showActivate}
          goToPrevious={(value: any) => {
            goToLogin(value.showMsg);
          }}
        />
      )}

      {msgConfig.snackFlag && (
        <div>
          <SnackBarMessage snacBarValue={msgConfig} />
        </div>
      )}
    </div>
  );
};
